import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import { lighten } from '../../../../shared/color';
import AnimatedProfileFollowButton from '../../../shared/animated-profile-follow-button';
import IconedButton from '../../../shared/iconed-button';
import EnquiryContext from '../../../shared/enquiry/context';
import { recordStatisticsEvent } from '../../../../services/statistics-service';

const Container = styled.div`
  flex-shrink: 0;

  ${props => props.theme.isLarge && `
    min-width: 132px;
  `};

  &&&&&& button {
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }

  & button > span:not(.icon-lw) {
    font-size: ${props => (props.theme.isLarge ? 14 : 12)}px;
  }

  & button > span.icon-lw-manage-wire + span,
  & button > span.icon-lw-edit + span {
    font-size: 12px;
  }

  & button[name="profile-follow-button"] {
    margin-top: 0;

    ${props => props.theme.isMobileOrTablet && `
      width: 107px;
      height: 33px;

      span.icon-lw-user-check {
        font-size: 19px;
        padding-right: 0;
      }
    `};

    ${props => props.theme.isLarge && `
      width: 132px;
    `};
  }

  ${props => (props.theme.isMobile ? `
    margin-top: -10px;
  ` : `
    & > button[name="profile-follow-button"] {
      margin-top: 24px;
    }
  `)};
`;

const Button = styled(IconedButton)`
  outline: none;
  margin-top: 24px;
  width: 148px;

  & > span.icon-lw {
    font-size: 16px;
    padding: 5px;
  }

  ${props => props.theme.isMobileOrTablet && `
    margin-top: 16px;
    width: 107px;
    height: 33px;
  `};

  ${props => props.theme.isMobile && `
    margin-top: 13px;
  `};
`;

const ManageMyWiresButton = styled(Button)`
  background-color: ${props => lighten(props.theme.primaryColor, 0.4)};

  &:hover {
    background-color: ${props => lighten(props.theme.primaryColor, 0.13)};
  }

  & > span.icon-lw {
    background-color: ${props => props.theme.primaryColor};
  }

  ${props => props.theme.isMobileOrTablet && `
    align-items: center;
    justify-content: center;
  `};

  ${props => props.theme.isMobile && `
    margin-top: 0px;
  `};
`;

const DarkButton = styled(Button)`
  background-color: #3C4966;
  margin-top: 12px;

  &:hover {
    background-color: #414651;
    color: ${props => props.theme.bodyBackgroundColor};
  }

  & > span.icon-lw {
    background-color: ${props => props.theme.bodyColor};
  }

  ${props => props.theme.isMobileOrTablet && `
    margin-top: 8px;
    align-items: center;
    justify-content: center;
  `};
`;

const ShortDarkButton = styled(DarkButton)`
  &&& {
    ${props => props.theme.isLarge && `
      width: 132px;
    `};
  }
`;

export default function Actions(props) {
  const { dispatch } = React.useContext(EnquiryContext);
  const { isMobileOrTablet } = React.useContext(ThemeContext);
  const {
    followed,
    profile: {
      id,
      activePartnership,
      activeFeatures,
    },
  } = props;

  const followButtonProps = {
    isProfileFollowedByCurrentUser: followed,
    profileId: id,
    followEventName: 'top_section_follow_profile',
    unfollowEventName: 'top_section_unfollow_profile',
  };

  const goto = url => e => {
    e && e.preventDefault();
    window.location = url;
  };

  const myProfile = goto('/profiles/me/personal_details');

  const myWires = goto('/profiles/me/my_wires/published');

  const showEnquiryForm = React.useCallback(() => {
    // without this offset, the comment body textarea element is at the top
    // edge of the screen, which is underneath the application navbar
    dispatch({ type: 'showEnquiryForm', payload: { scroll: true, offset: -200 } });
  }, [dispatch]);

  const handleContactClick = e => {
    e && e.preventDefault();

    recordStatisticsEvent({
      event: 'contributor_profile_top_section_contact_clicked',
      statisticable: 'profile',
      id,
      page: window.location.pathname,
    });

    if (_.get(window, 'Livewire.currentUser.id')) {
      showEnquiryForm();
    } else {
      window.location = '/sign_up?return_to_section=enquiry';
    }
  };

  React.useEffect(() => {
    if (window.location.hash === '#enquiry') {
      showEnquiryForm();
    }
  }, [showEnquiryForm]);

  const isPremiumEnquiryFormEnabled = _.includes(activeFeatures, 'premium_enquiry_form');
  const editText = isMobileOrTablet ? 'Edit' : 'Edit My Profile';
  const manageText = isMobileOrTablet ? 'Manage' : 'Manage My Wires';

  return (
    <Container>
      {props.isMyself
        ? (
          <>
            <ManageMyWiresButton type="button" className="btn btn-primary" onClick={myWires}>
              <span className="icon-lw icon-lw-manage-wire" />
              <span>{manageText}</span>
            </ManageMyWiresButton>
            <DarkButton type="button" className="btn" onClick={myProfile}>
              <span className="icon-lw icon-lw-edit" />
              <span>{editText}</span>
            </DarkButton>
          </>
        )
        : (
          <>
            <AnimatedProfileFollowButton {...followButtonProps} />
            {isPremiumEnquiryFormEnabled && !!activePartnership && (
              <ShortDarkButton type="button" className="btn" onClick={handleContactClick}>
                <span className="icon-lw icon-lw-contact" />
                <span>Contact</span>
              </ShortDarkButton>
            )}
          </>
        )}
    </Container>
  );
}

Actions.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    activeFeatures: PropTypes.arrayOf(PropTypes.string),
    activePartnership: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
  followed: PropTypes.bool.isRequired,
  isMyself: PropTypes.bool.isRequired,
};
